import { useMemo, useState } from 'react';
import { Link, useRoute } from 'wouter';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, AlertCircle, Users, Repeat } from 'lucide-react';
import { useActivationEvent } from '@/hooks/useActivationEvents';
import { useEventLeads } from '@/hooks/useActivationResults';

export default function LeadsPage() {
  const [, params] = useRoute('/activations/events/:eventId/leads');
  const eventId = params?.eventId;

  const { data: event } = useActivationEvent(eventId);
  const { data: leads, isLoading, isError, error, refetch } = useEventLeads(eventId);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const list = leads ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (l) =>
        (l.email ?? '').toLowerCase().includes(q) ||
        `${l.firstName ?? ''} ${l.lastName ?? ''}`.toLowerCase().includes(q),
    );
  }, [leads, search]);

  // Basic analytics: total unique leads + returning participants (activationCount > 1).
  const analytics = useMemo(() => {
    const list = leads ?? [];
    const total = list.length;
    const returning = list.filter((l) => (l.activationCount ?? 0) > 1).length;
    const returnRate = total > 0 ? Math.round((returning / total) * 100) : 0;
    return { total, returning, returnRate };
  }, [leads]);

  return (
    <div className="flex-1" data-testid="leads-page">
      <Header
        title={event ? `Leads — ${event.name}` : 'Leads'}
        description="Identified participants and returning-participant analytics."
      >
        <Button variant="outline" asChild>
          <Link href={`/activations/events/${eventId}/results`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to results
          </Link>
        </Button>
      </Header>

      <div className="space-y-6 p-6">
        {/* Analytics cards */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Card data-testid="analytics-total">
            <CardContent className="flex items-center gap-4 py-5">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{analytics.total}</p>
                <p className="text-sm text-muted-foreground">Unique leads</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="analytics-returning">
            <CardContent className="flex items-center gap-4 py-5">
              <Repeat className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{analytics.returning}</p>
                <p className="text-sm text-muted-foreground">Returning participants</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="analytics-rate">
            <CardContent className="flex items-center gap-4 py-5">
              <div>
                <p className="text-2xl font-bold">{analytics.returnRate}%</p>
                <p className="text-sm text-muted-foreground">Return rate</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="max-w-sm">
          <Input
            placeholder="Search leads…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="leads-search"
          />
        </div>

        {isLoading && <Skeleton className="h-48 w-full" data-testid="leads-loading" />}

        {isError && (
          <Alert variant="destructive" data-testid="leads-error">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load leads</AlertTitle>
            <AlertDescription className="flex items-center gap-3">
              {(error as Error)?.message || 'Something went wrong.'}
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <Card className="flex flex-col items-center gap-2 p-12 text-center" data-testid="leads-empty">
            <Users className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {search ? 'No leads match your search.' : 'No leads captured yet.'}
            </p>
          </Card>
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Activations</TableHead>
                  <TableHead>First seen</TableHead>
                  <TableHead>Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((l) => (
                  <TableRow key={l.userId} data-testid={`lead-row-${l.userId}`}>
                    <TableCell className="font-medium">
                      {[l.firstName, l.lastName].filter(Boolean).join(' ') || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{l.email}</TableCell>
                    <TableCell>{l.activationCount}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {l.firstSeenAt ? new Date(l.firstSeenAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {l.lastSeenAt ? new Date(l.lastSeenAt).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
}
